import {useEffect, useState} from 'react';
import {
  MiniCredentialCardProps,
  verifiedIconMap,
} from './miniCredential_types.web';
import {styles} from './miniCredential_styles.web';
import {useTheme} from '../../../../utils/theming/themeProvider';
import {isColorDark, isWhite} from '../../../../utils/colors';
import {canGetImage} from '../../../../utils/images';
import ImageError from '../../Icons/icons/imageError';
import Chip from '../../Chips/Chip/Chip.web';

const MiniCredentialCard = (props: MiniCredentialCardProps) => {
  const {
    id,
    className,
    issuer,
    verifiedIcon,
    containerStyle,
    display,
    chip,
    onClick,
  } = props;

  // Styles
  const {colors, fontStyles} = useTheme();
  const generalStyles = styles(
    colors,
    fontStyles,
    display?.background_color,
    !!verifiedIcon,
    !!display?.hasGradient,
  );
  const [imageCanCharge, setImageCanCharge] = useState<boolean | undefined>();
  const [chargingImage, setChagingImage] = useState<boolean | undefined>(true);

  const headingCredsRangeColor =
    display?.text_color?.toLowerCase() === '#ffffff' ||
    (display?.background_color && isColorDark(display?.background_color))
      ? 'light'
      : display?.text_color?.toLowerCase() === '#000000' ||
        (display?.background_color && !isColorDark(display?.background_color))
      ? 'dark'
      : display?.background_color && isColorDark(display?.background_color)
      ? 'light'
      : 'dark';

  const colorByRange =
    headingCredsRangeColor === 'dark' ? colors.neutral700 : colors.neutral300;

  const headingCredsColor =
    display?.text_color?.length &&
    display?.text_color.toLowerCase() !== '#ffffff' &&
    display?.text_color.toLowerCase() !== '#000000'
      ? display?.text_color
      : undefined;

  useEffect(() => {
    prepareIssuerImage();
  }, []);

  // IssuerImage
  async function prepareIssuerImage() {
    const imageCharge = !!issuer?.image && (await canGetImage(issuer?.image));
    if (!!issuer?.image && imageCharge) {
      setImageCanCharge(true);
    } else {
      setImageCanCharge(false);
    }
    setChagingImage(false);
  }

  // Verified Icon
  const VerifiedIconComponent = verifiedIcon && verifiedIconMap[verifiedIcon];

  const credBgColorIsDark =
    display?.background_color &&
    (!display?.text_color || headingCredsRangeColor === 'light') &&
    isColorDark(display?.background_color);

  const verifiedIconCustomProps =
    verifiedIcon === 'check'
      ? {
          color: !credBgColorIsDark ? colors.success300 : colors.success100,
          style: 'filled' as 'filled',
          checkColor: !credBgColorIsDark
            ? colors.neutral100
            : colors.success300,
        }
      : {
          color: !credBgColorIsDark ? colors.alert300 : colors.alert100,
          style: 'filled' as 'filled',
          crossColor: !credBgColorIsDark ? colors.neutral100 : colors.alert300,
        };

  const chipIsGrey = isWhite(display?.background_color || colors.neutral100);
  const chipColor =
    chip?.color === 'yellow'
      ? credBgColorIsDark
        ? 'yellow'
        : 'lightYellow'
      : chipIsGrey
      ? 'grey'
      : 'white';

  return (
    <div
      id={id}
      className={className}
      style={{
        ...generalStyles.credCardContainer,
        cursor: onClick ? 'pointer' : 'auto',
        ...containerStyle,
      }}
      onClick={onClick}>
      <div style={{...generalStyles.linearGradientOverlay}}>
        <div style={{...generalStyles.credCardMainContent}}>
          <div
            style={{
              ...generalStyles.credCardDetailRow,
              ...generalStyles.credCardTopData,
            }}>
            {issuer?.image ? (
              !!imageCanCharge ? (
                <img
                  src={issuer?.image}
                  alt="issuer"
                  style={{...generalStyles.credCardIssuerImage}}
                />
              ) : !chargingImage ? (
                <div
                  style={{
                    ...generalStyles.credIssuerImageError,
                  }}>
                  <ImageError
                    size={'16'}
                    color={
                      display?.text_color && !!headingCredsColor
                        ? display?.text_color
                        : credBgColorIsDark
                        ? colors.neutral300
                        : colors.neutral600
                    }
                  />
                  <p
                    style={{
                      color:
                        display?.text_color && !!headingCredsColor
                          ? display?.text_color
                          : credBgColorIsDark
                          ? colors.neutral300
                          : colors.neutral600,
                      ...fontStyles['indicatorSM'],
                      lineHeight: fontStyles['indicatorSM']?.lineHeight + 'px',
                      margin: 0,
                    }}>
                    {issuer?.failToLoadImageText || 'Fail to load image'}
                  </p>
                </div>
              ) : null
            ) : (
              <p
                style={{
                  color: headingCredsColor || colorByRange,
                  textAlign: 'left',
                  ...fontStyles['heading8'],
                  lineHeight: fontStyles['heading8']?.lineHeight + 'px',
                  alignSelf: 'flex-start',
                  margin: 0,
                }}>
                {issuer?.name}
              </p>
            )}
            {verifiedIcon && VerifiedIconComponent && (
              <div style={{...generalStyles.credCardVerifiedIcon}}>
                <VerifiedIconComponent
                  size={'8'}
                  {...verifiedIconCustomProps}
                />
              </div>
            )}
          </div>
        </div>
        {chip && (
          <Chip
            {...chip}
            color={chipColor}
            state={'enable'}
            style={'filled'}
            form={'square'}
            chipSize={'small'}
            showText={true}
            containerStyle={{
              alignSelf: 'flex-start',
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MiniCredentialCard;
